import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, ShieldAlert, X } from "lucide-react";
import { api, WeaponAlert } from "../api";
import { useScope } from "./ScopeContext";
import { useWeaponAlertsContext } from "./WeaponAlertsContext";
import {
  AlertCropImage,
  AlertSummary,
  alertStatusStyle,
  ribbonPremiumCopy,
} from "./weaponAlertsShared";

function AlertRibbon({
  alert,
  cameraName,
  storeName,
  onOpen,
  onClear,
  onEnlarge,
}: {
  alert: WeaponAlert;
  cameraName: string;
  storeName: string;
  onOpen: () => void;
  onClear: () => void;
  onEnlarge: () => void;
}) {
  const isPending = alert.status === "pending";
  const copy = ribbonPremiumCopy(alert, cameraName, storeName);
  const style = isPending
    ? alertStatusStyle("pending", alert)
    : alertStatusStyle("review", alert);
  const time = new Date(alert.updated_at_ms).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      title="View all alerts"
      className={`relative cursor-pointer overflow-hidden ${style.shell} bg-bf-surface/60 px-3 py-1.5 pr-8 transition hover:brightness-110 sm:px-4`}
    >
      {/* Left urgency accent bar */}
      <span className={`absolute inset-y-0 left-0 w-1 ${style.bar}`} aria-hidden />

      {/* Dismiss — circled X, flush top-right. Hides the ribbon until the
          next alarm; the alert itself stays in the Alerts page. */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClear();
        }}
        className="absolute right-1.5 top-1.5 z-10 grid h-5 w-5 place-items-center rounded-full bg-brand-purple/40 text-brand-fuchsia ring-1 ring-brand-fuchsia/70 transition hover:bg-brand-fuchsia/35 hover:text-fuchsia-300 hover:ring-fuchsia-300"
        aria-label="Dismiss"
      >
        <X size={11} strokeWidth={2.5} aria-hidden />
      </button>

      <div className="flex items-stretch gap-3">
        <AlertCropImage
          matchHeight
          alertId={alert.alert_id}
          alt={`Weapon alert ${alert.alert_id}`}
          buttonClassName={`shrink-0 self-stretch overflow-hidden rounded-lg shadow-lg shadow-black/40 ${style.thumbBorder} focus:outline-none focus:ring-2 focus:ring-bf-violet/60`}
          onClick={() => {
            onEnlarge();
          }}
        />

        <div className="min-w-0 flex-1">
          <AlertSummary
            label={copy.label}
            title={copy.title}
            message={copy.message}
            store={copy.store}
            camera={copy.camera}
            time={time}
            pillClass={style.pill}
            icon={<ShieldAlert size={24} className={style.iconBright} aria-hidden />}
            afterMessage={
              <span className="inline-flex shrink-0 items-baseline gap-1 text-sm text-white/80">
                <span>View details</span>
                <ChevronRight size={14} className="self-center" aria-hidden />
              </span>
            }
          />
        </div>
      </div>
    </div>
  );
}

export default function WeaponAlertsRibbon() {
  const nav = useNavigate();
  const { stores } = useScope();
  const { ribbonAlert, clearRibbon, setEnlarged } = useWeaponAlertsContext();
  const visibleRibbon = ribbonAlert;

  // Names for every camera, not just the scoped ones: the ribbon should still
  // read "Front door" for an alert that arrived just before a scope change.
  const cameras = useQuery({
    queryKey: ["cameras", "all"],
    queryFn: () => api.cameras.list(),
  });
  const cameraNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of cameras.data ?? []) map.set(c.id, c.name?.trim() || c.id);
    return map;
  }, [cameras.data]);
  const storeNameByKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of stores) map.set(s.name, s.display_name || s.name);
    return map;
  }, [stores]);
  const storeKeyByCameraId = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of cameras.data ?? []) {
      if (c.store) map.set(c.id, c.store);
    }
    return map;
  }, [cameras.data]);

  const barRef = useRef<HTMLDivElement>(null);
  const [barHeight, setBarHeight] = useState(0);

  useLayoutEffect(() => {
    const el = barRef.current;
    if (!el) {
      setBarHeight(0);
      return;
    }
    const apply = () => setBarHeight(el.offsetHeight);
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, [visibleRibbon]);

  // Ribbon shows only for a live, un-cleared alarm. Clearing hides it until the
  // next alarm; reviewed history is reached via the Alerts nav entry.
  if (!visibleRibbon) return null;

  const openAlerts = () => nav({ to: "/alerts", search: { tab: undefined } });
  const ribbon = (
    <AlertRibbon
      alert={visibleRibbon}
      cameraName={cameraNameById.get(visibleRibbon.camera_id) ?? visibleRibbon.camera_id}
      storeName={
        storeNameByKey.get(
          visibleRibbon.store || storeKeyByCameraId.get(visibleRibbon.camera_id) || "",
        ) ||
        visibleRibbon.store ||
        storeKeyByCameraId.get(visibleRibbon.camera_id) ||
        ""
      }
      onOpen={openAlerts}
      onClear={clearRibbon}
      onEnlarge={() => setEnlarged(visibleRibbon)}
    />
  );

  // Overlay the bar so it reads as a float; the spacer keeps the page
  // starting underneath it instead of sitting behind the wash.
  return (
    <>
      <div
        className="pointer-events-none shrink-0"
        style={{ height: barHeight > 0 ? barHeight + 20 : 0 }}
        aria-hidden
      />
      <div
        ref={barRef}
        className="absolute inset-x-3 top-3 z-30 overflow-hidden rounded-xl border border-white/15 shadow-xl shadow-black/50"
      >
        {ribbon}
      </div>
    </>
  );
}
